import React, { useState, useEffect } from "react";

/**
 * Avatar component with image caching and fallback to initials
 * @param {string} src - Image URL
 * @param {string} name - Person's name for initials
 * @param {string} className - Additional CSS classes
 * @param {string} alt - Alt text for image
 */
const Avatar = ({ src, name, className = "", alt }) => {
    const [imageSrc, setImageSrc] = useState(null);
    const [imageError, setImageError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    // Generate initials from name
    const getInitials = (name) => {
        if (!name) return "??";
        const parts = name.trim().split(" ");
        if (parts.length >= 2) {
            return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    // Generate consistent color from name
    const getColorFromName = (name) => {
        if (!name) return "bg-gray-400";

        const colors = [
            "bg-blue-500",
            "bg-green-500",
            "bg-purple-500",
            "bg-pink-500",
            "bg-indigo-500",
            "bg-teal-500",
            "bg-orange-500",
            "bg-cyan-500",
        ];

        const hash = name.split("").reduce((acc, char) => {
            return char.charCodeAt(0) + ((acc << 5) - acc);
        }, 0);

        return colors[Math.abs(hash) % colors.length];
    };

    useEffect(() => {
        if (!src) {
            setIsLoading(false);
            setImageError(true);
            return;
        }

        // Check if image is in cache (localStorage)
        const cacheKey = `avatar_cache_${src}`;
        const cachedImage = localStorage.getItem(cacheKey);

        if (cachedImage) {
            // Use cached image
            setImageSrc(cachedImage);
            setIsLoading(false);
            return;
        }

        // Load image and cache it
        const img = new Image();
        img.crossOrigin = "anonymous";

        img.onload = () => {
            try {
                // Create canvas to convert image to base64
                const canvas = document.createElement("canvas");
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0);

                // Convert to base64 and cache
                const base64Image = canvas.toDataURL("image/jpeg", 0.8);

                // Only cache if size is reasonable (< 500KB)
                if (base64Image.length < 500000) {
                    try {
                        localStorage.setItem(cacheKey, base64Image);
                    } catch (e) {
                        // If localStorage is full, clear old avatar caches
                        const keys = Object.keys(localStorage);
                        keys.forEach(key => {
                            if (key.startsWith("avatar_cache_") && key !== cacheKey) {
                                localStorage.removeItem(key);
                            }
                        });
                        // Try again
                        try {
                            localStorage.setItem(cacheKey, base64Image);
                        } catch (e) {
                            console.warn("Could not cache avatar image");
                        }
                    }
                }

                setImageSrc(base64Image);
                setIsLoading(false);
            } catch (error) {
                console.warn("Error caching image:", error);
                setImageSrc(src);
                setIsLoading(false);
            }
        };

        img.onerror = () => {
            setImageError(true);
            setIsLoading(false);
        };

        img.src = src;
    }, [src]);

    const initials = getInitials(name);
    const bgColor = getColorFromName(name);

    // Show initials if no image, error, or loading
    if (!src || imageError || isLoading) {
        return (
            <div
                className={`${className} ${bgColor} flex items-center justify-center text-white font-semibold select-none`}
                title={name}
            >
                {isLoading ? (
                    <div className="animate-pulse">...</div>
                ) : (
                    <span>{initials}</span>
                )}
            </div>
        );
    }

    return (
        <img
            src={imageSrc}
            alt={alt || name}
            className={className}
            onError={() => setImageError(true)}
        />
    );
};

export default Avatar;
